import { useState, useEffect, useRef, useMemo, useCallback } from "react";

export const CATEGORY_NAME_ALLOWED_REGEX = /^[a-zA-Z0-9\s\-_()]*$/;
export const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;
export const MAX_NAME_LENGTH = 100;
export const MIN_NAME_LENGTH = 3;

export const useCategoryModal = ({
  isOpen,
  onClose,
  onSave,
  category = null,
  existingCategories = [],
}) => {
  const isEdit = Boolean(category);

  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [gender, setGender] = useState("");
  const [mode, setMode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const nameInputRef = useRef(null);
  const typeSelectRef = useRef(null);
  const genderSelectRef = useRef(null);
  const modeSelectRef = useRef(null);

  const validateCategoryName = useCallback(
    (val, currentType = type) => {
      const raw = String(val || "");
      const cleanName = raw.trim().replace(/\s+/g, " ");

      if (!raw || !cleanName) {
        return "Category name is required.";
      }
      if (cleanName.length < MIN_NAME_LENGTH) {
        return `Category name must be at least ${MIN_NAME_LENGTH} characters.`;
      }
      if (cleanName.length > MAX_NAME_LENGTH) {
        return `Category name can be at most ${MAX_NAME_LENGTH} characters.`;
      }
      if (!CATEGORY_NAME_ALLOWED_REGEX.test(raw)) {
        return "Name can only contain letters, numbers, spaces, -, _, and ().";
      }
      if (!CONTAINS_LETTER_REGEX.test(cleanName)) {
        return "Name must contain at least one letter.";
      }

      if (existingCategories && Array.isArray(existingCategories)) {
        const cleanLower = cleanName.toLowerCase();
        const currentId = category?.id ?? category?.iCategoryId ?? null;
        const normalizedCurrentType = String(
          currentType || category?.sType || category?.type || ""
        )
          .trim()
          .toLowerCase();

        const isDuplicate = existingCategories.some((c) => {
          if (!c || !c.name) return false;
          const itemId = c.id ?? c.iCategoryId ?? null;
          if (
            currentId &&
            itemId &&
            String(itemId) === String(currentId)
          ) {
            return false;
          }

          // Same category name can exist across different category types (e.g. Filter vs Collection)
          // Only check duplicates if they have the same category type
          if (normalizedCurrentType) {
            const itemType = String(c.sType || c.type || "")
              .trim()
              .toLowerCase();
            if (itemType && itemType !== normalizedCurrentType) {
              return false;
            }
          }

          return c.name.trim().toLowerCase() === cleanLower;
        });

        if (isDuplicate) {
          const typeLabel =
            normalizedCurrentType === "collection"
              ? "collection"
              : normalizedCurrentType === "filter"
              ? "filter"
              : "category";
          return `A ${typeLabel} with this name already exists.`;
        }
      }

      return "";
    },
    [category, existingCategories, type]
  );

  const validateType = useCallback((val) => {
    if (!val || !String(val).trim()) {
      return "Please select a category type.";
    }
    if (!["filter", "collection"].includes(String(val).toLowerCase())) {
      return "Invalid category type selected.";
    }
    return "";
  }, []);

  const validateGender = useCallback((val) => {
    if (!val || !String(val).trim()) {
      return "Please select a gender.";
    }
    if (!["Women", "Men"].includes(val)) {
      return "Invalid gender selected.";
    }
    return "";
  }, []);

  const validateMode = useCallback((val) => {
    if (!val || !String(val).trim()) {
      return "Please select a mode.";
    }
    if (!["Model to Model", "Mannequin to Model"].includes(val)) {
      return "Invalid mode selected.";
    }
    return "";
  }, []);

  const isFormValid = useMemo(() => {
    const nameErr = validateCategoryName(name);
    const genderErr = validateGender(gender);
    const modeErr = validateMode(mode);
    const typeErr = !isEdit ? validateType(type) : "";

    return !nameErr && !genderErr && !modeErr && !typeErr;
  }, [
    name,
    gender,
    mode,
    type,
    isEdit,
    validateCategoryName,
    validateGender,
    validateMode,
    validateType,
  ]);

  const isFormModified = useMemo(() => {
    if (!category) return false;

    const initialName = (category.name || "").trim().replace(/\s+/g, " ");
    const currentName = name.trim().replace(/\s+/g, " ");
    const isNameChanged = currentName !== initialName;

    const initialGender = category.gender || "";
    const isGenderChanged = gender !== initialGender;

    const initialMode = category.mode || "";
    const isModeChanged = mode !== initialMode;

    const initialIsActive =
      category.isActive !== undefined ? Boolean(category.isActive) : true;
    const isStatusChanged = Boolean(isActive) !== initialIsActive;

    return isNameChanged || isGenderChanged || isModeChanged || isStatusChanged;
  }, [category, name, gender, mode, isActive]);

  const isSubmitDisabled =
    isSaving || !isFormValid || (isEdit && !isFormModified);

  const prevCategoryRef = useRef(category);
  const isFirstMountRef = useRef(true);

  const resetForm = useCallback(() => {
    if (category) {
      setName(category.name || "");
      const rawType = (category.sType || (category.type || "")).toLowerCase();
      setType(
        rawType === "collection"
          ? "collection"
          : rawType === "filter"
          ? "filter"
          : ""
      );
      setGender(category.gender || "");
      setMode(category.mode || "");
      setIsActive(category.isActive !== undefined ? Boolean(category.isActive) : true);
    } else {
      setName("");
      setType("");
      setGender("");
      setMode("");
      setIsActive(true);
    }
    setErrors({});
    setTouched({});
  }, [category]);

  const isFormDirty = useMemo(() => {
    if (isEdit) {
      return isFormModified;
    }
    return Boolean(
      (name && name.trim()) ||
        type ||
        gender ||
        mode ||
        !isActive ||
        Object.keys(errors).length > 0 ||
        Object.keys(touched).length > 0
    );
  }, [isEdit, isFormModified, name, type, gender, mode, isActive, errors, touched]);

  const isResetDisabled = isSaving || !isFormDirty;

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      if (category) {
        resetForm();
      }
      return;
    }

    const prevCat = prevCategoryRef.current;
    const prevId = prevCat?.id ?? prevCat?.iCategoryId ?? null;
    const currId = category?.id ?? category?.iCategoryId ?? null;

    const isCategoryChanged =
      prevId !== currId || Boolean(prevCat) !== Boolean(category);

    if (isCategoryChanged) {
      prevCategoryRef.current = category;
      resetForm();
    }
  }, [category, resetForm]);

  const handleReset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    resetForm();
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e && e.target !== e.currentTarget) return;
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (onClose) {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleNameChange = (e) => {
    const sanitized = e.target.value
      .replace(/^\s+/, "")
      .replace(/\s{2,}/g, " ");
    setName(sanitized);
    if (touched.name || errors.name) {
      const err = validateCategoryName(sanitized, type);
      setErrors((prev) => ({ ...prev, name: err }));
    }
  };

  const handleNameBlur = () => {
    setTouched((prev) => ({ ...prev, name: true }));
    const clean = name.trim();
    setName(clean);
    const err = validateCategoryName(clean, type);
    setErrors((prev) => ({ ...prev, name: err }));
  };

  const handleTypeChange = (e) => {
    const val = e.target.value;
    setType(val);
    if (touched.type || errors.type) {
      setErrors((prev) => ({ ...prev, type: validateType(val) }));
    }
    // Re-validate category name when type changes because duplicate validation is per category type
    if (name) {
      const nameErr = validateCategoryName(name, val);
      setErrors((prev) => ({ ...prev, name: nameErr }));
    }
  };

  const handleTypeBlur = () => {
    if (!isEdit) {
      setTouched((prev) => ({ ...prev, type: true }));
      setErrors((prev) => ({ ...prev, type: validateType(type) }));
    }
  };

  const handleGenderChange = (e) => {
    const val = e.target.value;
    setGender(val);
    setTouched((prev) => ({ ...prev, gender: true }));
    setErrors((prev) => ({ ...prev, gender: validateGender(val) }));
  };

  const handleGenderBlur = () => {
    setTouched((prev) => ({ ...prev, gender: true }));
    setErrors((prev) => ({ ...prev, gender: validateGender(gender) }));
  };

  const handleModeChange = (e) => {
    const val = e.target.value;
    setMode(val);
    setTouched((prev) => ({ ...prev, mode: true }));
    setErrors((prev) => ({ ...prev, mode: validateMode(val) }));
  };

  const handleModeBlur = () => {
    setTouched((prev) => ({ ...prev, mode: true }));
    setErrors((prev) => ({ ...prev, mode: validateMode(mode) }));
  };

  const validateAll = () => {
    const errs = {};
    const nameErr = validateCategoryName(name);
    if (nameErr) errs.name = nameErr;

    if (!isEdit) {
      const typeErr = validateType(type);
      if (typeErr) errs.type = typeErr;
    }

    const genderErr = validateGender(gender);
    if (genderErr) errs.gender = genderErr;

    const modeErr = validateMode(mode);
    if (modeErr) errs.mode = modeErr;

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      name: true,
      type: true,
      gender: true,
      mode: true,
    });

    const validationErrors = validateAll();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors.name && nameInputRef.current) {
        nameInputRef.current.focus();
      } else if (validationErrors.type && typeSelectRef.current) {
        typeSelectRef.current.focus();
      } else if (validationErrors.gender && genderSelectRef.current) {
        genderSelectRef.current.focus();
      } else if (validationErrors.mode && modeSelectRef.current) {
        modeSelectRef.current.focus();
      }
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        id: category ? category.id : null,
        name: name.trim().replace(/\s+/g, " "),
        type,
        gender,
        mode,
        isActive,
      });
      resetForm();
      if (onClose) {
        onClose();
      }
    } catch {
      // error handled in caller/hook; keep modal open
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isEdit,
    name,
    setName,
    type,
    setType,
    gender,
    setGender,
    mode,
    setMode,
    isActive,
    setIsActive,
    errors,
    setErrors,
    touched,
    setTouched,
    isSaving,
    isFormValid,
    isFormModified,
    isFormDirty,
    isSubmitDisabled,
    isResetDisabled,
    nameInputRef,
    typeSelectRef,
    genderSelectRef,
    modeSelectRef,
    handleNameChange,
    handleNameBlur,
    handleTypeChange,
    handleTypeBlur,
    handleGenderChange,
    handleGenderBlur,
    handleModeChange,
    handleModeBlur,
    handleSubmit,
    handleReset,
    handleCancel,
    handleBackdropClick,
    validateCategoryName,
    validateType,
    validateGender,
    validateMode,
    MAX_NAME_LENGTH,
    MIN_NAME_LENGTH,
  };
};

export default useCategoryModal;
